/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: =, Brian Bal
 */

import { useHistory } from "react-router"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux"

import { Page } from "../Navigation"
import StoryMoment from "../StoryMoment"
import { GamesPageHeader } from "../../components/GamesPageHeader"
import TeamGameItem from "./TeamGameItem.js"
import { pageTitle } from "trux"
import {
    Row,
    Element,
    Label,
    Color,
    Column,
    TraceTeamInfoCard,
    useToken,
} from "trux"
import * as stat from "../../util/analytics"
import tracebot from "../../assets/img/waving_tracebot.svg"
import { useContentAccessForTeamLazyQuery } from "../../generated/graphql"
import { WrappedConvertChurnedDialog } from "../WrappedConvertChurnedDialog"
import "./GameList.css"

const GameListComp = ({
    currentTeam,
    teamLoaded,
    loading,
    isTeamLoggedIn,
    isLoggedIn,
    showReferal,
    userDivisions,
    user,
    games,
    ...props
}) => {
    const history = useHistory()

    const [showChurnedDialog, setShowChurnedDialog] = useState(false)

    // Get a token
    const token = useToken("Game List Page")

    // On mount
    useEffect(() => {
        stat.statPageView()
        if (currentTeam) {
            pageTitle(currentTeam.title, "Team - Game List")
        }
        window.scrollTo(0, 0)
    }, [])

    // Onupdate
    useEffect(() => {
        if (teamLoaded === true && !currentTeam) {
            console.log("Redirect to /", currentTeam, teamLoaded)
            history.replace("/")
        }
        if (currentTeam) {
            pageTitle(currentTeam.title, "Team - Game List")
        }
    }, [currentTeam, teamLoaded])

    const handleShowTraceTeamDialog = () => {
        history.push(history.location.pathname + "/upgrade-trace-team")
    }

    const getGameItems = () => {
        var gameItems = []
        if (loading || games === null) {
            for (var i = 0; i < 3; i++) {
                gameItems.push(
                    <div
                        key={`team-game-list-item-placeholder-${i}`}
                        className="TeamGameItem placeholder"
                    />
                )
            }
        } else {
            let someGames = []
            if (!isTeamLoggedIn) {
                someGames = games.filter((g) => {
                    let keep = g.list_status === "ready"
                    if (g.subtasks_statuses && g.subtasks_statuses.emails) {
                        keep = g.subtasks_statuses.emails.status === "processed"
                    }
                    return keep
                })
            } else {
                someGames = games
            }

            gameItems = someGames.map((game) => {
                return (
                    <TeamGameItem
                        key={game.game_id}
                        game={game}
                        isLoggedIn={isLoggedIn}
                        history={history}
                    />
                )
            })
            if (gameItems.length > 5 && showReferal) {
                gameItems.splice(
                    6,
                    0,
                    <div className="refer-friend-game-item">
                        <Column padding="4" center>
                            <a
                                href={`https://traceup.com/refer?utm_source=go&utm_medium=webapp&utm_campaign=refs&referred_by=${
                                    user && user.email ? user.email : ""
                                }`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-trk-act="Refer Friend"
                                data-trk-lbl="Referral Button"
                            >
                                Tell someone about Trace, Earn $100
                            </a>
                            <Label
                                type={Label.Type.Body}
                                color={Color.Grey2}
                                small
                            >
                                * We will not show this message to players
                            </Label>
                        </Column>
                    </div>
                )
            }
        }
        return gameItems
    }

    const isTraceFamilyTeam =
        currentTeam && currentTeam.division?.type === "family" ? true : false
    const isUserOnTeam =
        userDivisions &&
        userDivisions.some(
            (division) =>
                division?.division_id === currentTeam?.division?.division_id
        )
    const showTraceTeamInfoCard = isUserOnTeam && isTraceFamilyTeam
    var gameItems = getGameItems()

    const [fetchContentAccess] = useContentAccessForTeamLazyQuery({})

    useEffect(() => {
        // Get the content access
        if (currentTeam && token) {
            fetchContentAccess({
                variables: {
                    team_id: currentTeam?.team_id,
                    token: token,
                },
                skip: !props.token || !currentTeam,
            }).then((contentAccess) => {
                const access = contentAccess?.data?.contentAccessForTeam
                const allowed = access ? access.allowed : true
                const shouldShow =
                    !allowed && !window.location.href?.includes("success")
                setShowChurnedDialog(shouldShow)
            })
        }
    }, [user, token, currentTeam])

    return (
        <Element className="GameList" data-trk-ctx="GameList">
            {showChurnedDialog && (
                <WrappedConvertChurnedDialog
                    videoContainerRef={null}
                    hideCloseButton={true}
                />
            )}
            <Page teamName={props.match.params.team_name}>
                <GamesPageHeader />

                {gameItems.length > 0 && (
                    <Row
                        justifyContent="center"
                        alignItems="center"
                        marginLeft="4"
                        marginRight="4"
                    >
                        <StoryMoment trackingAction="Game List Page" />
                    </Row>
                )}

                <Row
                    className="GameItems"
                    justifyContent="center"
                    alignItems="center"
                    wrap
                >
                    {gameItems}
                </Row>

                {gameItems.length === 0 && (
                    <Element className="no-games-container">
                        <Element
                            tag="img"
                            className="tracebot-image"
                            src={tracebot}
                            alt="tracebot waving img"
                        />
                        <Element>
                            <Label>You don&apos;t have any games yet.</Label>
                        </Element>
                    </Element>
                )}
                {showTraceTeamInfoCard && (
                    <TraceTeamInfoCard
                        renderAsRow={false}
                        onButtonClick={(e) => handleShowTraceTeamDialog(e)}
                    />
                )}
            </Page>
        </Element>
    )
}

const mapStateToProps = (state) => {
    let games = state.games.value
    if (!games) {
        games = []
    }
    games = games.filter((g) => g.list_status !== "hide")

    let showReferal = false
    let isCoach = state.team.value && state.team.value.access_password
    let isMobileSafari = /(iPhone|iPod|iPad)/i.test(navigator.userAgent)

    if (state.user.value && state.team.value && state.team.value.members) {
        for (let member of state.team.value.members) {
            if (state.user.value.user_id === member.user_id) {
                // eslint-disable-next-line eqeqeq
                showReferal = member.is_player != 1
            }
        }
    }

    return {
        loading: state.games.loading,
        userDivisions: state.divisions.value,
        games: games,
        isLoggedIn: state.isLoggedIn,
        isCoach: isCoach,
        isMobileSafari: isMobileSafari,
        isTeamLoggedIn: state.isTeamLoggedIn,
        teamLoaded: state.team.complete,
        currentTeam: state.team.value,
        user: state.user.value,
        showReferal: showReferal,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

const GameListPage = connect(mapStateToProps, mapDispatchToProps)(GameListComp)

export default GameListPage
